// Performance optimization utilities for mobile web app

// Lazy loading for images
export const lazyLoadImage = (src: string, placeholder?: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = reject;
        img.src = src;
    });
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (
    callback: (entries: IntersectionObserverEntry[]) => void,
    options?: IntersectionObserverInit
) => {
    if (!('IntersectionObserver' in window)) {
        // Fallback for browsers without IntersectionObserver
        return null;
    }

    return new IntersectionObserver(callback, {
        rootMargin: '50px',
        threshold: 0.1,
        ...options
    });
};

// Debounce function for performance-heavy operations
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

// Throttle function for scroll/resize events
export const throttle = <T extends (...args: any[]) => any>(
    func: T,
    limit: number
): ((...args: Parameters<T>) => void) => {
    let inThrottle = false;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

// Preload critical resources
export const preloadResource = (href: string, as: string, type?: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    if (type) link.type = type;
    document.head.appendChild(link);
};

// Resource hints for better performance
export const addResourceHints = () => {
    // DNS prefetch for external domains
    const prefetchDomains = [
        'https://fonts.googleapis.com',
        'https://www.google-analytics.com',
        'https://www.googletagmanager.com',
        'https://play.google.com',
        'https://tzzrexadedvxpkdahoyj.supabase.co'
    ];

    prefetchDomains.forEach(domain => {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = domain;
        document.head.appendChild(link);
    });

    // Preload critical fonts
    preloadResource('/fonts/roboto-v30-latin-regular.woff2', 'font', 'font/woff2');
    preloadResource('/fonts/roboto-v30-latin-500.woff2', 'font', 'font/woff2');
};

// Optimize images for different screen sizes
export const getOptimizedImageSrc = (baseSrc: string, width: number, quality = 80): string => {
    // This would typically use a CDN like Cloudinary or ImageKit
    // For now, return the original source
    return baseSrc;
};

// Critical CSS inlining helper
export const inlineCriticalCSS = (css: string) => {
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);
};

// Service Worker registration
export const registerServiceWorker = async (): Promise<void> => {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register('/sw.js');

            // Handle updates
            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener('statechange', () => {
                        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                            // New content available, show update notification
                            showUpdateAvailableNotification();
                        }
                    });
                }
            });
        } catch (error) {
    // Console statement removed
  }
    }
};

// Show update available notification
const showUpdateAvailableNotification = () => {
    const updateBanner = document.createElement('div');
    updateBanner.innerHTML = `
        <div style="
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: #A76FFF;
            color: white;
            padding: 12px 16px;
            text-align: center;
            z-index: 9999;
            font-family: system-ui, -apple-system, sans-serif;
        ">
            <span>New version available!</span>
            <button onclick="window.location.reload()" style="
                background: white;
                color: #A76FFF;
                border: none;
                padding: 4px 12px;
                margin-left: 12px;
                border-radius: 4px;
                cursor: pointer;
                font-weight: 500;
            ">Update</button>
            <button onclick="this.parentElement.parentElement.remove()" style="
                background: transparent;
                color: white;
                border: 1px solid white;
                padding: 4px 12px;
                margin-left: 8px;
                border-radius: 4px;
                cursor: pointer;
            ">Later</button>
        </div>
    `;
    document.body.prepend(updateBanner);
};

// Performance monitoring
export const measurePerformance = () => {
    if ('performance' in window) {
        // Core Web Vitals
        const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
                if (entry.entryType === 'largest-contentful-paint') {
    // Console statement removed
  }

                if (entry.entryType === 'first-input') {
    // Console statement removed
  }

                if (entry.entryType === 'layout-shift') {
                    if (!(entry as any).hadRecentInput) {
                    }
                }
            });
        });

        try {
            observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
        } catch (e) {
            // Browser doesn't support all metrics
        }

        // Time to Interactive approximation
        setTimeout(() => {
            const tti = performance.now();
        }, 0);
    }
};

// Network status monitoring
export const getNetworkStatus = (): { effectiveType: string; downlink: number; saveData: boolean } => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

    if (connection) {
        return {
            effectiveType: connection.effectiveType || 'unknown',
            downlink: connection.downlink || 0,
            saveData: connection.saveData || false
        };
    }

    return {
        effectiveType: 'unknown',
        downlink: 0,
        saveData: false
    };
};

// Adaptive loading based on network conditions
export const shouldLoadHeavyContent = (): boolean => {
    const network = getNetworkStatus();

    // Don't load heavy content on slow connections or data saver mode
    if (network.saveData || network.effectiveType === 'slow-2g' || network.effectiveType === '2g') {
        return false;
    }

    return true;
};

// Memory usage monitoring (Chrome only)
export const getMemoryUsage = () => {
    if ('memory' in performance) {
        const memory = (performance as any).memory;
        return {
            usedJSHeapSize: memory.usedJSHeapSize,
            totalJSHeapSize: memory.totalJSHeapSize,
            jsHeapSizeLimit: memory.jsHeapSizeLimit
        };
    }
    return null;
};

// Battery API monitoring
export const getBatteryStatus = async () => {
    if ('getBattery' in navigator) {
        try {
            const battery = await (navigator as any).getBattery();
            return {
                charging: battery.charging,
                level: battery.level,
                dischargingTime: battery.dischargingTime
            };
        } catch (error) {
            return null;
        }
    }
    return null;
};

// Initialize all performance optimizations
export const initializePerformanceOptimizations = () => {
    // Add resource hints
    addResourceHints();

    // Register service worker
    registerServiceWorker();

    // Start performance monitoring
    measurePerformance();

    // Log initial performance metrics

    const memoryUsage = getMemoryUsage();
    if (memoryUsage) {
    // Console statement removed
  }
};